import { useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';

/**
 * Remember where somebody was, so signing back in returns them there.
 *
 * Worth knowing what this does and does not cover. An expired session does NOT
 * redirect: api.js clears the tokens and the app re-renders the login screen at
 * the CURRENT url (App's logged-out tree matches "*"), so signing back in lands
 * on the same page by itself. What this adds is the other route back — closing
 * the browser and returning to the bare domain, which otherwise always opened
 * the dashboard.
 */

const KEY = 'pf_last_path';

// A fortnight-old deep link is not a convenience, it is a surprise. Long enough
// to cover a holiday weekend, short enough that the destination probably still
// exists.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Recording any of these would either loop (landing -> stored landing) or send
// somebody back to a screen that only makes sense once.
const SKIP = [/^\/$/, /^\/login\b/, /^\/auth\//];

/** Is this something we are willing to send a browser to? */
const isSafe = (path) => {
  if (typeof path !== 'string' || !path.startsWith('/')) return false;
  // "//evil.com" is a protocol-relative URL, not a path — it starts with "/"
  // and would leave the site. The stored value lives in the user's own
  // localStorage so this is not much of an attack, but sending a browser
  // somewhere it was never meant to go is worth one line to rule out.
  if (path.startsWith('//') || path.includes('://')) return false;
  return !SKIP.some((re) => re.test(path));
};

export function saveLastPath(path) {
  if (!isSafe(path)) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Private browsing, or storage disabled. Losing the breadcrumb is fine;
    // throwing here would take the whole render with it.
  }
}

export function readLastPath() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw);
    // Re-validated on the way OUT as well as in: what was written may predate
    // a rule change, or have been edited by hand.
    if (!isSafe(path)) return null;
    if (!at || Date.now() - at > MAX_AGE_MS) return null;
    return path;
  } catch {
    return null;
  }
}

export function clearLastPath() {
  try {
    localStorage.removeItem(KEY);
  } catch { /* see saveLastPath */ }
}

/**
 * Records the current location. Mounted once, inside the signed-in tree.
 *
 * On every navigation rather than on sign-out: an idle timeout never runs a
 * sign-out handler, and neither does closing the tab.
 */
export function LastPathRecorder() {
  const location = useLocation();
  useEffect(() => {
    saveLastPath(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

/**
 * What "/" resolves to for a signed-in user: the last page if there is a
 * usable one, the dashboard otherwise.
 *
 * A stored path that no longer resolves is not a problem to solve here — the
 * routes already handle their own unknown cases (the requisition tabs fall
 * back to their dashboard when the id is unknown or the role is missing).
 */
export function LandingRedirect({ fallback = '/dashboard' }) {
  const target = readLastPath() || fallback;
  return <Navigate to={target} replace />;
}
